import type { CategoryId, Venue } from './catalog';

export interface SeatIdentity {
  categoryId: CategoryId;
  venueId: string;
  venueName: string;
  spaceId: string;
  spaceName: string;
  layoutVersionId: string;
  instanceId: string | null;
  instanceLabel: string | null;
  sectionCode: string;
  rowLabel: string;
  seatNumber: number;
}

export interface SeatCandidate extends SeatIdentity {
  candidateId: string;
  expectedPrice: number | null;
}

export interface SeatCoordinateInput {
  sectionCode: string;
  rowLabel: string;
  seatNumber: number | string;
}

export type SeatIdentityErrorCode =
  | 'E_UNRESOLVED_SECTION'
  | 'E_ROW_REQUIRED'
  | 'E_SEAT_NUMBER';

export class SeatIdentityError extends Error {
  constructor(public readonly code: SeatIdentityErrorCode, message: string) {
    super(message);
  }
}

export function normalizeLabel(value: string): string {
  return value.trim().replace(/[\s-]+/g, '').toLocaleUpperCase('ko-KR');
}

export function normalizeSeatKey(
  venue: Pick<Venue, 'id'>,
  layoutVersionId: string,
  input: SeatCoordinateInput,
  knownSections?: ReadonlyMap<string, string>,
): string {
  const rawSection = input.sectionCode.trim();
  if (!rawSection) throw new SeatIdentityError('E_UNRESOLVED_SECTION', '구역을 넣어 주세요.');

  const normalizedRaw = normalizeLabel(rawSection);
  const section = knownSections ? knownSections.get(normalizedRaw) : normalizedRaw;
  if (knownSections && !section) {
    throw new SeatIdentityError('E_UNRESOLVED_SECTION', '등록된 구역 이름과 맞지 않아요. 티켓 표기를 확인해 주세요.');
  }

  const row = normalizeLabel(input.rowLabel);
  if (!row) throw new SeatIdentityError('E_ROW_REQUIRED', '열을 넣어 주세요.');

  const seatNumber = Number(input.seatNumber);
  if (!Number.isInteger(seatNumber) || seatNumber < 1 || seatNumber > 9999) {
    throw new SeatIdentityError('E_SEAT_NUMBER', '좌석번호는 1 이상의 숫자로 넣어 주세요.');
  }
  return `${venue.id}:${layoutVersionId}:${section}:${row}:${seatNumber}`;
}

export function formatSeatLabel(seat: Pick<SeatIdentity, 'sectionCode' | 'rowLabel' | 'seatNumber'>): string {
  return `${seat.sectionCode} ${seat.rowLabel}열 ${seat.seatNumber}번`;
}

export function candidateId(): string {
  return globalThis.crypto.randomUUID();
}
